// src/features/appointments/BookingPage.tsx
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  CheckCircle, ChevronRight, ChevronLeft, Calendar, User,
  Clock, AlertCircle, Stethoscope, Phone, Building2, Sun, Moon,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { doctorService, departmentService, appointmentService, receptionService, slotService } from '../../api/services';
import {
  AvailableSlotDto, CreateAppointmentRequest, FlexibleSlotDto,
  DoctorListDto, DepartmentDto,
} from '../../api/types';
import { useAuthStore } from '../auth/auth.store';
import { useSettingsStore } from '../settings/settings.store';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { statusLabel } from '../../utils/statusLabel';

/* ────────────────────────────── Types ────────────────────────────── */

type BookingMode = 'department' | 'doctor' | 'session';

interface BookingState {
  doctorId?: number;
  departmentId?: number;
  slotDate?: string;
  sessionType?: string;
  patientName: string;
  patientPhone: string;
  patientGender: string;
  patientEmail: string;
  patientNotes: string;
  isOnCall?: boolean;
}

// Slot cutoff rules: Morning/Evening slots are hidden after their configurable cutoff hour
function isSlotBookable(
  slotDate: string,
  sessionType: string,
  morningCutoff: number,
  eveningCutoff: number,
): boolean {
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  if (slotDate !== todayStr) return true;
  const hour = new Date().getHours();
  if (sessionType === 'Morning' && hour >= morningCutoff) return false;
  if (sessionType === 'Evening' && hour >= eveningCutoff) return false;
  return true;
}

const MODE_STEPS: Record<BookingMode, { label: string }[]> = {
  department: [
    { label: 'Speciality' },
    { label: 'Slot' },
    { label: 'Details' },
  ],
  doctor: [
    { label: 'Doctor' },
    { label: 'Slot' },
    { label: 'Details' },
  ],
  session: [
    { label: 'Session' },
    { label: 'Slot' },
    { label: 'Details' },
  ],
};

/* ────────────────────────────── Component ────────────────────────── */

export function BookingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const morningCutoff = useSettingsStore((s) => s.morningCutoffHour);
  const eveningCutoff = useSettingsStore((s) => s.eveningCutoffHour);

  // Reception/Admin can book today's slots regardless of cutoff time
  const isStaffBooking = user?.role === 'Admin' || user?.role === 'Reception';
  const effectiveMorningCutoff = isStaffBooking ? 24 : morningCutoff;
  const effectiveEveningCutoff = isStaffBooking ? 24 : eveningCutoff;

  const preselectedDoctorId = searchParams.get('doctorId') ? Number(searchParams.get('doctorId')) : undefined;
  const preselectedDeptId = searchParams.get('departmentId') ? Number(searchParams.get('departmentId')) : undefined;

  const [mode, setMode] = useState<BookingMode | null>(preselectedDoctorId ? 'doctor' : null);
  const [step, setStep] = useState(preselectedDoctorId ? 2 : 1);
  const [booking, setBooking] = useState<BookingState>({
    doctorId: preselectedDoctorId,
    departmentId: preselectedDeptId,
    patientName: user?.name || '',
    patientPhone: user?.phone || '',
    patientGender: '',
    patientEmail: '',
    patientNotes: '',
  });
  const [confirmedAppointment, setConfirmedAppointment] = useState<any>(null);
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorListDto | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<DepartmentDto | null>(null);
  const [sessionFilter, setSessionFilter] = useState<'Morning' | 'Evening'>('Morning');
  const [doctorDeptFilter, setDoctorDeptFilter] = useState<number | undefined>(undefined);

  const hasPreselection = !!preselectedDoctorId;
  const isReceptionUser = user?.role === 'Reception' || user?.role === 'Admin';

  /* ── Data queries ───────────────────────────────────────────────── */

  const { data: departments = [], isLoading: deptsLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: departmentService.getAll,
    enabled: mode !== null,
  });

  // Mode: doctor — step 1 doctor list (all doctors, optional dept filter)
  const { data: doctors = [], isLoading: doctorsLoading } = useQuery({
    queryKey: ['doctors', { departmentId: doctorDeptFilter }],
    queryFn: () => doctorService.getAll({ departmentId: doctorDeptFilter, acceptingBookings: true }),
    enabled: mode === 'doctor' && step === 1,
  });

  // Mode: doctor — step 3 per-doctor slots
  const { data: doctorSlots = [], isLoading: doctorSlotsLoading } = useQuery({
    queryKey: ['slots', booking.doctorId],
    queryFn: () => doctorService.getAvailableSlots(booking.doctorId!),
    enabled: mode === 'doctor' && !!booking.doctorId && step === 2,
  });

  // Mode: department — step 2 flexible slots
  const { data: deptSlots = [], isLoading: deptSlotsLoading } = useQuery({
    queryKey: ['flex-slots-dept', booking.departmentId],
    queryFn: () => slotService.getByDepartment(booking.departmentId!),
    enabled: mode === 'department' && !!booking.departmentId && step === 2,
    staleTime: 60_000,
  });

  // Mode: session — step 2 flexible slots
  const { data: sessionSlots = [], isLoading: sessionSlotsLoading } = useQuery({
    queryKey: ['flex-slots-session', sessionFilter, booking.departmentId],
    queryFn: () => slotService.getBySession(sessionFilter, booking.departmentId),
    enabled: mode === 'session' && step === 2,
    staleTime: 60_000,
  });

  /* ── Submit mutation ────────────────────────────────────────────── */

  const { mutate: submitBooking, isPending: submitting } = useMutation({
    mutationFn: (req: CreateAppointmentRequest) =>
      isReceptionUser ? receptionService.bookForPatient(req) : appointmentService.create(req),
    onSuccess: (data) => {
      setConfirmedAppointment(data);
      goToConfirmation();
      if (isReceptionUser) {
        toast.success(
          data?.tokenNumber
            ? `Appointment confirmed — Token #${data.tokenNumber}`
            : 'Appointment confirmed for patient'
        );
      } else {
        toast.success('Appointment booked! Awaiting confirmation.');
      }
    },
    onError: (err: any) => {
      toast.error(err.message || 'Booking failed');
    },
  });

  /* ── Navigation helpers ─────────────────────────────────────────── */

  // On-call doctors in doctor mode skip the slot step
  const effectiveSteps = mode
    ? (mode === 'doctor' && booking.isOnCall
      ? MODE_STEPS.doctor.filter(s => s.label !== 'Slot')
      : MODE_STEPS[mode])
    : [];
  const detailsStep = effectiveSteps.length;
  const confirmationStep = detailsStep + 1;

  function goToConfirmation() {
    setStep(confirmationStep);
  }

  /* ── Handlers ───────────────────────────────────────────────────── */

  const selectMode = (m: BookingMode) => {
    setMode(m);
    setStep(1);
    setBooking({
      patientName: user?.name || '',
      patientPhone: user?.phone || '',
      patientGender: '',
      patientEmail: '',
      patientNotes: '',
    });
    setSelectedDoctor(null);
    setSelectedDepartment(null);
    setDoctorDeptFilter(undefined);
  };

  const selectDepartment = (d: DepartmentDto) => {
    setSelectedDepartment(d);
    setBooking((b) => ({ ...b, departmentId: d.id }));
    setStep(2);
  };

  const selectDoctor = (d: DoctorListDto) => {
    setSelectedDoctor(d);
    setBooking((b) => ({ ...b, doctorId: d.id, departmentId: d.departmentId, isOnCall: d.isOnCall }));
    setStep(2);
  };

  const selectDoctorSlot = (slot: AvailableSlotDto) => {
    setBooking((b) => ({ ...b, slotDate: slot.slotDate, sessionType: slot.sessionType }));
    setStep(detailsStep);
  };

  const selectFlexibleSlot = (slot: FlexibleSlotDto) => {
    setSelectedDoctor({ name: slot.doctorName, departmentName: slot.departmentName } as DoctorListDto);
    setBooking((b) => ({
      ...b,
      doctorId: slot.doctorId,
      departmentId: slot.departmentId,
      slotDate: slot.slotDate,
      sessionType: slot.sessionType,
      isOnCall: slot.isOnCall,
    }));
    setStep(detailsStep);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!booking.doctorId) return;
    if (!booking.isOnCall && (!booking.slotDate || !booking.sessionType)) return;

    submitBooking({
      doctorId: booking.doctorId,
      departmentId: booking.departmentId,
      patientName: booking.patientName,
      patientPhone: booking.patientPhone,
      patientGender: booking.patientGender || undefined,
      patientEmail: booking.patientEmail || undefined,
      patientNotes: booking.patientNotes || undefined,
      ...(booking.isOnCall ? {} : {
        appointmentDate: booking.slotDate,
        sessionType: booking.sessionType,
      }),
      bookingSource: isReceptionUser ? 'Reception' : 'Website',
    });
  };

  const resetWizard = () => {
    setMode(null);
    setStep(1);
    setBooking({
      patientName: user?.name || '',
      patientPhone: user?.phone || '',
      patientGender: '',
      patientEmail: '',
      patientNotes: '',
    });
    setConfirmedAppointment(null);
    setSelectedDoctor(null);
    setSelectedDepartment(null);
  };

  /* ── Slot grouping helpers ──────────────────────────────────────── */

  const doctorSlotsByDate = doctorSlots
    .filter(slot => isSlotBookable(slot.slotDate, slot.sessionType, effectiveMorningCutoff, effectiveEveningCutoff))
    .reduce<Record<string, AvailableSlotDto[]>>((acc, slot) => {
      (acc[slot.slotDate] ??= []).push(slot);
      return acc;
    }, {});

  const flexSlotsByDate = (mode === 'department' ? deptSlots : sessionSlots)
    .filter(slot => isSlotBookable(slot.slotDate, slot.sessionType, effectiveMorningCutoff, effectiveEveningCutoff))
    .reduce<Record<string, FlexibleSlotDto[]>>((acc, slot) => {
      (acc[slot.slotDate] ??= []).push(slot);
      return acc;
    }, {});

  const sessionSlotsByDoctor = sessionSlots
    .filter(slot => isSlotBookable(slot.slotDate, slot.sessionType, effectiveMorningCutoff, effectiveEveningCutoff))
    .reduce<Map<number, FlexibleSlotDto>>((map, slot) => {
      if (!map.has(slot.doctorId)) map.set(slot.doctorId, slot);
      return map;
    }, new Map());

  /* ── Step indicator ─────────────────────────────────────────────── */

  const visibleSteps = mode
    ? effectiveSteps.map((s, i) => ({ label: s.label, display: i + 1 }))
    : [];

  const showSteps = mode !== null && step <= detailsStep;

  /* ── Render ─────────────────────────────────────────────────────── */

  return (
    <div className="max-w-4xl mx-auto px-6 py-6">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-slate-900">Book an Appointment</h1>
        <p className="text-sm text-slate-500 mt-1">
          {mode
            ? 'Follow the steps below to schedule your visit'
            : 'Choose how you\'d like to find your appointment'}
        </p>
      </div>

      {/* Step indicator */}
      {showSteps && (
        <div className="flex items-center gap-0 mb-8">
          {visibleSteps.map((s, idx) => (
            <div key={idx} className="flex items-center">
              <div
                className={clsx(
                  'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200',
                  step === idx + 1
                    ? 'bg-brand-600 text-white shadow-sm'
                    : step > idx + 1
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-100 text-slate-400'
                )}
              >
                {step > idx + 1 ? (
                  <CheckCircle className="w-3.5 h-3.5" />
                ) : (
                  <span className="w-4 h-4 flex items-center justify-center text-[11px] font-bold">
                    {s.display}
                  </span>
                )}
                {s.label}
              </div>
              {idx < visibleSteps.length - 1 && (
                <ChevronRight className="w-4 h-4 text-slate-300 mx-1" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* ══════════════════════ MODE SELECTOR ══════════════════════ */}
      {mode === null && (
        <div className="animate-slide-in">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">How would you like to book?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <button
              onClick={() => selectMode('department')}
              className="card p-5 text-left hover:shadow-card-hover hover:border-brand-200 transition-all duration-200 group"
            >
              <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center mb-3">
                <Building2 className="w-5 h-5 text-brand-700" />
              </div>
              <div className="font-semibold text-slate-900 group-hover:text-brand-700 transition-colors text-sm">
                By Speciality
              </div>
              <p className="text-xs text-slate-500 mt-1">Browse available slots by department</p>
            </button>

            <button
              onClick={() => selectMode('doctor')}
              className="card p-5 text-left hover:shadow-card-hover hover:border-brand-200 transition-all duration-200 group"
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-3">
                <Stethoscope className="w-5 h-5 text-emerald-700" />
              </div>
              <div className="font-semibold text-slate-900 group-hover:text-brand-700 transition-colors text-sm">
                By Doctor
              </div>
              <p className="text-xs text-slate-500 mt-1">Choose a specific doctor first</p>
            </button>

            <button
              onClick={() => selectMode('session')}
              className="card p-5 text-left hover:shadow-card-hover hover:border-brand-200 transition-all duration-200 group"
            >
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-3">
                <Clock className="w-5 h-5 text-amber-700" />
              </div>
              <div className="font-semibold text-slate-900 group-hover:text-brand-700 transition-colors text-sm">
                By Time
              </div>
              <p className="text-xs text-slate-500 mt-1">Find doctors available in morning or evening</p>
            </button>
          </div>
        </div>
      )}

      {/* ══════════════ MODE: DEPARTMENT ══════════════ */}

      {mode === 'department' && step === 1 && (
        <div className="animate-slide-in">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => setMode(null)} className="btn btn-ghost btn-sm">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-brand-600" />
              Select a Speciality
            </h2>
          </div>

          {deptsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="card p-4 space-y-3">
                  <div className="skeleton h-5 w-2/3" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {departments.map((d) => (
                <button
                  key={d.id}
                  onClick={() => selectDepartment(d)}
                  className="card p-4 text-left transition-all duration-200 group hover:shadow-card-hover hover:border-brand-200 cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="font-semibold text-slate-900 group-hover:text-brand-700 transition-colors text-sm">{d.name}</div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-brand-500 transition-colors flex-shrink-0 mt-0.5" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === 'department' && step === 2 && (
        <div className="animate-slide-in">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => setStep(1)} className="btn btn-ghost btn-sm">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-brand-600" />
              Available Slots
            </h2>
          </div>

          {selectedDepartment && (
            <div className="flex items-center gap-2 p-2.5 bg-brand-50 rounded-xl border border-brand-100 mb-5 text-xs text-brand-700 font-medium">
              <Building2 className="w-3.5 h-3.5" />
              {selectedDepartment.name}
            </div>
          )}

          {deptSlotsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <div key={i} className="skeleton h-24 rounded-xl" />)}
            </div>
          ) : deptSlots.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-slate-500">No available slots in the next 7 days</p>
              <button onClick={() => setStep(1)} className="btn btn-secondary btn-sm mt-3">Choose another speciality</button>
            </div>
          ) : (
            <div className="space-y-5">
              {Object.entries(flexSlotsByDate).map(([date, daySlots]) => (
                <div key={date}>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
                    {format(parseISO(date), 'EEEE, MMMM d')}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {(daySlots as FlexibleSlotDto[]).map((slot, i) => (
                      <button
                        key={i}
                        onClick={() => selectFlexibleSlot(slot)}
                        className="card p-3 text-left hover:shadow-card-hover hover:border-brand-200 transition-all duration-150 group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-brand-50 flex items-center justify-center text-brand-700 font-bold text-xs flex-shrink-0">
                              {slot.doctorName.charAt(0)}
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-slate-900 group-hover:text-brand-700 transition-colors">
                                {slot.doctorTitle && `${slot.doctorTitle} `}{slot.doctorName}
                              </div>
                              <div className="text-xs text-slate-500">{slot.doctorQualifications}</div>
                            </div>
                          </div>
                          {slot.isOnCall && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium flex-shrink-0">On-Call</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-brand-700 font-medium mt-2">
                          <Clock className="w-3 h-3" />
                          {slot.sessionType} · {slot.startTime} – {slot.endTime}
                        </div>
                        {slot.isOnCall && (
                          <div className="text-[10px] text-amber-600 mt-1">Timings are approximate</div>
                        )}
                        <div className="mt-2 flex items-center gap-1">
                          <div className={clsx(
                            'h-1 rounded-full flex-1 transition-all',
                            slot.remainingSlots > 5 ? 'bg-emerald-400' : slot.remainingSlots > 2 ? 'bg-amber-400' : 'bg-red-400'
                          )} style={{ width: `${(slot.remainingSlots / slot.maxPatients) * 100}%` }} />
                          <div className="h-1 bg-slate-100 rounded-full flex-1" />
                          <span className={clsx(
                            'text-[10px] font-semibold ml-1',
                            slot.remainingSlots > 5 ? 'text-emerald-600' : slot.remainingSlots > 2 ? 'text-amber-600' : 'text-red-600'
                          )}>{slot.remainingSlots} left</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ MODE: DOCTOR ══════════════ */}

      {mode === 'doctor' && step === 1 && (
        <div className="animate-slide-in">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => setMode(null)} className="btn btn-ghost btn-sm">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-brand-600" />
              Choose a Doctor
            </h2>
            <div className="ml-auto">
              <select
                value={doctorDeptFilter ?? ''}
                onChange={(e) => setDoctorDeptFilter(e.target.value ? Number(e.target.value) : undefined)}
                className="input text-sm py-1.5 px-3 pr-8"
              >
                <option value="">All Specialities</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          </div>

          {doctorsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="card p-4 space-y-3">
                  <div className="skeleton h-4 w-2/3" />
                  <div className="skeleton h-3 w-1/2" />
                </div>
              ))}
            </div>
          ) : doctors.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-slate-500">No doctors found</p>
              {doctorDeptFilter && (
                <button onClick={() => setDoctorDeptFilter(undefined)} className="btn btn-secondary btn-sm mt-3">
                  Show all specialities
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {doctors.map((d) => (
                <button
                  key={d.id}
                  onClick={() => selectDoctor(d)}
                  disabled={!d.isAcceptingBookings}
                  className={clsx(
                    'card p-4 text-left transition-all duration-200 group',
                    d.isAcceptingBookings ? 'hover:shadow-card-hover hover:border-brand-200 cursor-pointer' : 'opacity-60 cursor-not-allowed'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-brand-50 flex items-center justify-center text-brand-700 font-bold flex-shrink-0">
                      {d.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-slate-900 group-hover:text-brand-700 transition-colors text-sm">
                          {d.title && `${d.title} `}{d.name}
                        </div>
                        {d.isOnCall && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">On-Call</span>
                        )}
                      </div>
                      <div className="text-xs text-brand-600 font-medium mt-0.5">{d.departmentName}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{d.qualifications}</div>
                      {d.experience && <div className="text-xs text-slate-400 mt-0.5">{d.experience}</div>}
                      {d.isOnCall && (
                        <div className="text-xs text-amber-600 mt-1">Subject to availability — reception will confirm</div>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-brand-500 transition-colors flex-shrink-0 mt-1" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === 'doctor' && step === 2 && !booking.isOnCall && (
        <div className="animate-slide-in">
          <div className="flex items-center gap-3 mb-4">
            {!hasPreselection && (
              <button onClick={() => setStep(1)} className="btn btn-ghost btn-sm">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            )}
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-brand-600" />
              Choose a Slot
            </h2>
          </div>

          {selectedDoctor && (
            <div className="flex items-center gap-3 p-3 bg-brand-50 rounded-xl border border-brand-100 mb-5">
              <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold text-sm">
                {selectedDoctor.name.charAt(0)}
              </div>
              <div>
                <div className="font-semibold text-slate-900 text-sm">
                  {selectedDoctor.title && `${selectedDoctor.title} `}{selectedDoctor.name}
                </div>
                <div className="text-xs text-slate-500">{selectedDoctor.departmentName}</div>
              </div>
              {booking.isOnCall && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium ml-auto">On-Call</span>
              )}
            </div>
          )}

          {booking.isOnCall && (
            <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 mb-4 text-xs text-amber-700">
              Slot timings are approximate for on-call doctors. Reception will call to confirm your appointment.
            </div>
          )}

          {doctorSlotsLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => <div key={i} className="skeleton h-24 rounded-xl" />)}
            </div>
          ) : doctorSlots.length === 0 && !booking.isOnCall ? (
            <div className="text-center py-12">
              <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-slate-500">No available slots in the next 7 days</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(doctorSlotsByDate).map(([date, daySlots]) => (
                <div key={date}>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
                    {format(parseISO(date), 'EEEE, MMMM d')}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {daySlots.map((slot, i) => (
                      <button
                        key={i}
                        onClick={() => selectDoctorSlot(slot)}
                        className="card p-3 text-left hover:shadow-card-hover hover:border-brand-200 transition-all duration-150 group"
                      >
                        <div className="flex items-center gap-2 text-xs font-semibold text-brand-700 group-hover:text-brand-800">
                          <Clock className="w-3.5 h-3.5" />
                          {slot.sessionType}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">{slot.startTime} – {slot.endTime}</div>
                        <div className="mt-2 flex items-center gap-1">
                          <div className={clsx(
                            'h-1 rounded-full flex-1 transition-all',
                            slot.remainingSlots > 5 ? 'bg-emerald-400' : slot.remainingSlots > 2 ? 'bg-amber-400' : 'bg-red-400'
                          )} style={{ width: `${(slot.remainingSlots / slot.maxPatients) * 100}%` }} />
                          <div className="h-1 bg-slate-100 rounded-full flex-1" />
                          <span className={clsx(
                            'text-[10px] font-semibold ml-1',
                            slot.remainingSlots > 5 ? 'text-emerald-600' : slot.remainingSlots > 2 ? 'text-amber-600' : 'text-red-600'
                          )}>{slot.remainingSlots} left</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ MODE: SESSION ══════════════ */}

      {mode === 'session' && step === 1 && (
        <div className="animate-slide-in">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => setMode(null)} className="btn btn-ghost btn-sm">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Clock className="w-5 h-5 text-brand-600" />
              Choose a Session
            </h2>
          </div>

          {(() => {
            const hour = new Date().getHours();
            const morningClosed = hour >= effectiveMorningCutoff;
            const eveningClosed = hour >= effectiveEveningCutoff;
            return (
          <div className="card p-5 space-y-5">
            <div>
              <label className="label mb-2">Preferred Time</label>
              <div className="flex gap-3">
                <button
                  onClick={() => !morningClosed && setSessionFilter('Morning')}
                  disabled={morningClosed}
                  className={clsx(
                    'flex-1 card p-4 text-center transition-all duration-200',
                    morningClosed
                      ? 'opacity-50 cursor-not-allowed bg-slate-50'
                      : sessionFilter === 'Morning'
                      ? 'border-brand-500 bg-brand-50 shadow-sm'
                      : 'hover:border-brand-200'
                  )}
                >
                  <Sun className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                  <div className="font-semibold text-sm text-slate-900">Morning</div>
                  <div className="text-xs text-slate-500">
                    {morningClosed ? 'Booking closed (after 12 PM)' : 'Before 12 PM'}
                  </div>
                </button>
                <button
                  onClick={() => !eveningClosed && setSessionFilter('Evening')}
                  disabled={eveningClosed}
                  className={clsx(
                    'flex-1 card p-4 text-center transition-all duration-200',
                    eveningClosed
                      ? 'opacity-50 cursor-not-allowed bg-slate-50'
                      : sessionFilter === 'Evening'
                      ? 'border-brand-500 bg-brand-50 shadow-sm'
                      : 'hover:border-brand-200'
                  )}
                >
                  <Moon className="w-6 h-6 text-indigo-500 mx-auto mb-1" />
                  <div className="font-semibold text-sm text-slate-900">Evening</div>
                  <div className="text-xs text-slate-500">
                    {eveningClosed ? 'Booking closed (after 3 PM)' : 'After 12 PM'}
                  </div>
                </button>
              </div>
            </div>

            <div>
              <label className="label mb-2">Department (optional)</label>
              <select
                className="input"
                value={booking.departmentId ?? ''}
                onChange={(e) => setBooking((b) => ({ ...b, departmentId: e.target.value ? Number(e.target.value) : undefined }))}
              >
                <option value="">All Departments</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            <button onClick={() => setStep(2)} className="btn btn-primary w-full justify-center btn-lg">
              Find Available Doctors <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          );
          })()}
        </div>
      )}

      {mode === 'session' && step === 2 && (
        <div className="animate-slide-in">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => setStep(1)} className="btn btn-ghost btn-sm">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-brand-600" />
              {sessionFilter} Slots
            </h2>
          </div>

          <div className="flex items-center gap-2 p-2.5 bg-brand-50 rounded-xl border border-brand-100 mb-5 text-xs text-brand-700 font-medium">
            {sessionFilter === 'Morning' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            {sessionFilter} session
          </div>

          {sessionSlotsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <div key={i} className="skeleton h-24 rounded-xl" />)}
            </div>
          ) : sessionSlots.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-slate-500">No {sessionFilter.toLowerCase()} slots available in the next 7 days</p>
              <button onClick={() => setStep(1)} className="btn btn-secondary btn-sm mt-3">Change session</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Array.from(sessionSlotsByDoctor.values()).map((slot) => (
                <button
                  key={slot.doctorId}
                  onClick={() => selectFlexibleSlot(slot)}
                  className="card p-4 text-left hover:shadow-card-hover hover:border-brand-200 transition-all duration-150 group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center text-brand-700 font-bold text-sm flex-shrink-0">
                      {slot.doctorName.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-slate-900 group-hover:text-brand-700 transition-colors text-sm">
                          {slot.doctorTitle && `${slot.doctorTitle} `}{slot.doctorName}
                        </div>
                        {slot.isOnCall && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">On-Call</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{slot.departmentName}</div>
                      <div className="flex items-center gap-2 text-xs text-brand-700 font-medium mt-2">
                        <Calendar className="w-3 h-3" />
                        {format(parseISO(slot.slotDate), 'EEE, MMM d')}
                        <Clock className="w-3 h-3 ml-1" />
                        {slot.startTime} – {slot.endTime}
                      </div>
                      <div className="mt-2 flex items-center gap-1">
                        <div className={clsx(
                          'h-1 rounded-full flex-1 transition-all',
                          slot.remainingSlots > 5 ? 'bg-emerald-400' : slot.remainingSlots > 2 ? 'bg-amber-400' : 'bg-red-400'
                        )} style={{ width: `${(slot.remainingSlots / slot.maxPatients) * 100}%` }} />
                        <div className="h-1 bg-slate-100 rounded-full flex-1" />
                        <span className={clsx(
                          'text-[10px] font-semibold ml-1',
                          slot.remainingSlots > 5 ? 'text-emerald-600' : slot.remainingSlots > 2 ? 'text-amber-600' : 'text-red-600'
                        )}>{slot.remainingSlots} left</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ SHARED: PATIENT DETAILS ══════════════ */}
      {mode !== null && step === detailsStep && (
        <div className="animate-slide-in">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => setStep(step - 1)} className="btn btn-ghost btn-sm">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <User className="w-5 h-5 text-brand-600" />
              Your Details
            </h2>
          </div>

          {(booking.slotDate || booking.isOnCall) && (
            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl border border-slate-200 mb-5 text-xs text-slate-600 flex-wrap">
              <span className="flex items-center gap-1">
                <Stethoscope className="w-3.5 h-3.5 text-brand-500" />
                {selectedDoctor?.name}
              </span>
              {booking.slotDate && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-brand-500" />
                  {format(parseISO(booking.slotDate), 'EEE, MMM d')}
                </span>
              )}
              {booking.sessionType && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-brand-500" />
                  {booking.sessionType} session
                </span>
              )}
              {booking.isOnCall && (
                <span className="flex items-center gap-1 text-amber-600 font-medium">On-Call — date to be confirmed by reception</span>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="card p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Full Name *</label>
                <input className="input" value={booking.patientName} onChange={(e) => setBooking((b) => ({ ...b, patientName: e.target.value }))} placeholder="Your full name" required />
              </div>
              <div>
                <label className="label">Mobile Number *</label>
                <div className="relative">
                  <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input className="input pl-9" type="tel" value={booking.patientPhone} onChange={(e) => setBooking((b) => ({ ...b, patientPhone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} placeholder="10-digit mobile" pattern="[6-9][0-9]{9}" required />
                </div>
              </div>
              <div>
                <label className="label">Gender</label>
                <select className="input" value={booking.patientGender} onChange={(e) => setBooking((b) => ({ ...b, patientGender: e.target.value }))}>
                  <option value="">Prefer not to say</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="label">Email (optional)</label>
                <input className="input" type="email" value={booking.patientEmail} onChange={(e) => setBooking((b) => ({ ...b, patientEmail: e.target.value }))} placeholder="email@example.com" />
              </div>
            </div>
            <div>
              <label className="label">Notes / Reason for visit</label>
              <textarea className="input resize-none h-20" value={booking.patientNotes} onChange={(e) => setBooking((b) => ({ ...b, patientNotes: e.target.value }))} placeholder="Briefly describe your symptoms or reason for visit..." />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setStep(step - 1)} className="btn btn-secondary">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <button type="submit" disabled={submitting} className="btn btn-primary flex-1 justify-center btn-lg">
                {submitting ? 'Booking...' : (<>Confirm Booking <ChevronRight className="w-4 h-4" /></>)}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ══════════════ SHARED: CONFIRMATION ══════════════ */}
      {mode !== null && step === confirmationStep && confirmedAppointment && (
        <div className="animate-fade-up text-center max-w-md mx-auto">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 ${
            confirmedAppointment.bookingSource === 'Reception' ? 'bg-emerald-100' : 'bg-brand-100'
          }`}>
            <CheckCircle className={`w-10 h-10 ${
              confirmedAppointment.bookingSource === 'Reception' ? 'text-emerald-600' : 'text-brand-600'
            }`} strokeWidth={1.5} />
          </div>

          {confirmedAppointment.bookingSource === 'Reception' ? (
            <>
              <h2 className="text-2xl font-semibold text-slate-900 mb-1">Appointment Confirmed!</h2>
              {confirmedAppointment.tokenNumber ? (
                <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-5 py-2 rounded-full text-lg font-bold mb-5">
                  Token #{confirmedAppointment.tokenNumber}
                </div>
              ) : (
                <p className="text-sm text-amber-600 mb-5 font-medium">
                  Token will be assigned when date is confirmed (on-call doctor)
                </p>
              )}
            </>
          ) : (
            <>
              <h2 className="text-2xl font-semibold text-slate-900 mb-2">
                {confirmedAppointment.isOnCallBooking ? 'Booking Request Received!' : 'Appointment Requested!'}
              </h2>
              <p className="text-sm text-slate-500 mb-6 leading-relaxed">
                {confirmedAppointment.isOnCallBooking
                  ? `Our reception team will contact you at your phone number within 2 hours to confirm your appointment date and time with ${confirmedAppointment.doctorName}.`
                  : 'Your appointment request has been submitted. Reception will review and confirm it shortly.'}
              </p>
            </>
          )}

          <div className="card p-6 text-left mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {confirmedAppointment.bookingSource === 'Reception' && (
                <div className="col-span-2">
                  <div className="label">Patient</div>
                  <div className="font-semibold text-slate-900">{confirmedAppointment.patientName}</div>
                  <div className="text-xs text-slate-500">{confirmedAppointment.patientPhone}</div>
                </div>
              )}
              <div>
                <div className="label">Appointment #</div>
                <div className="font-mono font-semibold text-brand-700">{confirmedAppointment.appointmentNumber}</div>
              </div>
              <div>
                <div className="label">Status</div>
                <span className={`status-${confirmedAppointment.status}`}>{statusLabel(confirmedAppointment.status)}</span>
              </div>
              <div>
                <div className="label">Doctor</div>
                <div className="font-medium text-slate-800">{confirmedAppointment.doctorName}</div>
              </div>
              <div>
                <div className="label">Date</div>
                <div className="font-medium text-slate-800">
                  {confirmedAppointment.appointmentDate
                    ? format(parseISO(confirmedAppointment.appointmentDate), 'EEE, MMM d yyyy')
                    : <span className="text-amber-600">To be confirmed</span>}
                </div>
              </div>
              <div>
                <div className="label">Session</div>
                <div className="font-medium text-slate-800">
                  {confirmedAppointment.sessionType ?? <span className="text-amber-600">TBC</span>}
                </div>
              </div>
              <div>
                <div className="label">Department</div>
                <div className="font-medium text-slate-800">{confirmedAppointment.departmentName}</div>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            {confirmedAppointment.bookingSource === 'Reception' ? (
              <button onClick={() => navigate('/reception/queue')} className="btn btn-secondary flex-1 justify-center">
                View Queue
              </button>
            ) : (
              <button onClick={() => navigate('/appointments')} className="btn btn-secondary flex-1 justify-center">
                My Appointments
              </button>
            )}
            <button onClick={resetWizard} className="btn btn-primary flex-1 justify-center">
              Book Another
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
